import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabaseClient';
import { PROFILE_COLUMNS, type ProfileVisibleRow } from '../lib/db-types';

/** Ruta donde la persona se crea su propia clave. Es la única exenta del bloqueo. */
export const RUTA_CAMBIAR_CLAVE = '/cambiar-clave';

export interface AuthState {
  user: User | null;
  profile: ProfileVisibleRow | null;
  roles: string[];
  /**
   * Recorre ruta: es lo que decide si ve «Mi ruta». Sale de `profiles.hace_ruta` y NO del rol,
   * porque el rol `coordinador` lo comparten quien recorre tiendas y quien coordina desde la
   * oficina. Ante la duda va en `false`: una pestaña de más la ve alguien a quien no le toca; el
   * servidor rechaza igual a quien no corresponde.
   *
   * OPCIONAL por la misma razón que `mustChangePassword`, y con el mismo criterio: ausente
   * significa «no se sabe», y no saber nunca habilita. Así los cientos de sitios que arman un
   * `AuthState` en pruebas no tienen que declarar una bandera que no les importa.
   */
  haceRuta?: boolean;
  loading: boolean;
  /**
   * `true` = la persona todavía usa la clave que le entregaron (`profiles.must_change_password`)
   * y no puede entrar a ninguna ruta protegida hasta crear la suya.
   *
   * OPCIONAL a propósito: ausente significa "no se sabe", y no saber NUNCA bloquea. La
   * migración 0015 puede no estar aplicada todavía (el MCP de Supabase estaba caído), y en
   * ese caso la lectura de la columna falla y aquí no llega nada. Un sistema que deja fuera
   * a los 11 del piloto por una migración pendiente es peor que la clave compartida.
   */
  mustChangePassword?: boolean;
  /** La llama la pantalla de cambio de clave al terminar, para soltar el bloqueo en el acto. */
  marcarClaveCambiada?: () => void;
}

const EMPTY: AuthState = { user: null, profile: null, roles: [], haceRuta: false, loading: true };

/** Exportado para poder inyectar estado en tests. */
export const AuthContext = createContext<AuthState>(EMPTY);

export function useAuth(): AuthState {
  return useContext(AuthContext);
}

/**
 * Lee `profiles.must_change_password` en una consulta APARTE, y no dentro de
 * `PROFILE_COLUMNS`, por una razón concreta: si la columna todavía no existe, un select que
 * la incluyera fallaría ENTERO (42703) y dejaría el perfil en nulo — sin perfil no hay roles,
 * y sin roles nadie entra a ninguna parte. Aislada, lo peor que puede pasar es que no se sepa.
 *
 * Cualquier error se traduce como `false` (no bloquear): 42703 si falta la columna, 42501 si
 * existe pero le falta el `grant select` por columna que hace la 0015 — 0002_rls.sql revoca el
 * select de tabla sobre `profiles` y lo otorga columna por columna.
 */
async function leerClavePendiente(userId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('profiles')
    .select('must_change_password')
    .eq('id', userId)
    .maybeSingle();
  if (error) return false;
  return (data as { must_change_password?: boolean } | null)?.must_change_password === true;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(EMPTY);
  // Sobrevive a las recargas de perfil de esta sesión: `updateUser` dispara un evento
  // USER_UPDATED que vuelve a leer el perfil, y esa lectura podría cruzarse con el UPDATE
  // que pone la marca en false y devolver el valor viejo. Con esto, una vez cambiada la
  // clave en esta sesión, ninguna relectura vuelve a bloquear.
  const claveYaCambiada = useRef(false);

  useEffect(() => {
    let cancelled = false;

    /**
     * Orden de los avisos de sesión. Cada lectura de perfil se queda con el número que tenía el
     * aviso que la originó, y una lectura vieja NUNCA pisa a una más nueva.
     *
     * POR QUÉ HACE FALTA — el bug que arregla: el perfil se carga desde DOS lugares que no se
     * coordinan, el `getSession()` del arranque y el `onAuthStateChange`. Al abrir la app sin
     * sesión, `getSession()` sale con `null`; si ese `null` llega TARDE —después del `SIGNED_IN`
     * del login, cosa normal en una conexión lenta— borraba el usuario recién puesto y dejaba
     * el estado en `{ user: null, loading: false }`. El guard leía eso como "no hay sesión" y
     * devolvía al login: **el primer intento de ingreso no entraba, sin mostrar ningún error y
     * con los campos vacíos**, y al segundo sí, porque ya no había carrera. Reproducido en
     * producción con la traza `/login → replace:/panel/hoy → replace:/login`.
     *
     * Lo que manda es el ORDEN, no el valor: un `SIGNED_OUT` posterior sí tiene que sacar a la
     * persona. Por eso se compara el número y no se ignoran los nulos.
     */
    let ultimaGeneracion = 0;

    async function loadProfile(user: User | null, generacion: number) {
      /** ¿Llegó algo más nuevo mientras esta lectura estaba en vuelo? */
      const quedoVieja = () => cancelled || generacion < ultimaGeneracion;

      if (!user) {
        if (!quedoVieja()) {
          setState({ user: null, profile: null, roles: [], haceRuta: false, loading: false });
        }
        return;
      }
      // PROFILE_COLUMNS y NO select('*'): la Task 4 revoca a `authenticated` el privilegio
      // sobre bank/account_number/cci, así que un select('*') falla con 42501.
      const { data, error } = await supabase
        .from('profiles')
        .select(PROFILE_COLUMNS)
        .eq('id', user.id)
        .maybeSingle();
      if (quedoVieja()) return;

      // Un fallo de RED al releer el perfil NO es una pérdida de acceso.
      // `onAuthStateChange` se dispara al refrescar el token (cada ~50 min), al volver del
      // bloqueo del celular y al cambiar de wifi a datos. Si en ese momento la consulta
      // falla —cosa habitual dentro de una tienda—, poner el perfil en null dejaba a la
      // persona sin roles y el guard la expulsaba al login: parecía que "se cerró la
      // sesión sola" cuando la sesión seguía perfectamente válida.
      // Ante error, se conserva lo último que se supo. La sesión solo se limpia cuando
      // Supabase avisa que ya no hay usuario (el caso `!user` de arriba).
      if (error) {
        setState((anterior) => ({ ...anterior, user, loading: false }));
        return;
      }

      const profile = data as ProfileVisibleRow | null;

      // ⚠️⚠️ AQUÍ NO SE CIERRA LA SESIÓN, Y ES UNA CICATRIZ: se intentó y rompió el login.
      //
      // La idea era razonable —"si no hay ficha, la cuenta se borró, suelta la sesión"— pero
      // parte de una premisa falsa: **bajo RLS, "todavía no tengo permiso" y "no existe"
      // devuelven exactamente lo mismo**, `data: null` sin error. Y justo después de entrar hay
      // un instante en que la consulta sale sin que PostgREST reconozca aún la sesión, así que
      // el perfil vuelve vacío. Cerrar sesión ahí borraba la sesión recién creada un segundo
      // después de crearse, y la persona volvía al login. Se vio en la traza del navegador:
      //     CLIC Ingresar → SESION: true → SESION: false → /login
      //
      // Una lectura vacía NUNCA puede cerrar una sesión. Es la misma familia de regla que la de
      // arriba con los errores de red, y por la misma razón: lo que no se sabe no se castiga.
      // Si de verdad la cuenta no tiene ficha, los roles quedan vacíos y el guard lo dirá; es un
      // caso raro (una cuenta borrada por detrás) y molesta menos que echar a alguien real.
      const roles = profile && profile.status === 'activo' ? profile.roles : [];
      const pendiente = claveYaCambiada.current ? false : await leerClavePendiente(user.id);
      if (quedoVieja()) return;
      setState({
        user, profile, roles, loading: false, mustChangePassword: pendiente,
        haceRuta: profile?.hace_ruta === true && profile.status === 'activo',
      });
    }

    // El número se toma cuando NACE el aviso, no cuando termina su lectura: el `getSession()`
    // del arranque es siempre el primero, aunque conteste el último.
    const generacionInicial = ++ultimaGeneracion;
    supabase.auth.getSession().then(({ data }) =>
      loadProfile(data.session?.user ?? null, generacionInicial),
    );

    // ⚠️ EL CALLBACK NO PUEDE LLAMAR A SUPABASE. Se anota qué pasó y se sale; el trabajo real
    // va en un `setTimeout(0)`, o sea en el turno siguiente del bucle de eventos.
    //
    // POR QUÉ, y qué rompía: supabase-js serializa el manejo de sesión con `navigator.locks` y
    // ejecuta ESTE callback dentro de ese lock. `loadProfile` consulta `profiles`, esa consulta
    // necesita el token, pedirlo vuelve a pedir el MISMO lock… que no se libera hasta que el
    // callback termine. Se traban entre sí.
    //
    // Se vio en el Chrome del usuario: al pulsar "Cerrar sesión" el token desaparecía del
    // almacenamiento pero `signOut()` no resolvía nunca, así que el `navigate('/login')` que
    // viene después ni se ejecutaba y la aplicación seguía pintando el panel sin sesión. En el
    // login el mismo bloqueo dejaba el estado sin actualizarse el tiempo suficiente para que el
    // guard concluyera que no había sesión y devolviera al formulario — **el primer intento no
    // entraba y el segundo sí**, que es exactamente lo que se reportó.
    //
    // El número de generación se toma AQUÍ, no dentro del `setTimeout`: lo que ordena los avisos
    // es cuándo ocurrieron, no cuándo se alcanzó a atenderlos.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      const user = session?.user ?? null;
      const generacion = ++ultimaGeneracion;

      // EL USUARIO SE PUBLICA EN EL ACTO. Ya viene dentro del aviso: no hay ninguna razón para
      // esperar a la consulta del perfil para saber que hay sesión.
      //
      // Es lo que arregla el "primer intento no entra" de verdad. Al entrar, `Login.tsx`
      // autentica, lee el perfil por su cuenta y navega a `/panel/hoy`; en paralelo, este
      // proveedor sale a leer el perfil, que es una consulta de red de cientos de milisegundos.
      // Durante ese hueco el estado decía `user: null, loading: false` — y `loading: false`
      // significa "ya terminé de mirar y no hay nadie", así que el guard lo leía literalmente y
      // hacía `<Navigate to="/login" />`. La sesión SÍ estaba creada (por eso al reabrir la
      // pestaña ya se estaba dentro), pero la pantalla volvía al formulario.
      //
      // Esto es solo React: no llama a Supabase, así que es seguro hacerlo dentro del callback
      // sin volver a caer en el bloqueo de sesión.
      setState((anterior) => {
        // El cierre de sesión lo resuelve `loadProfile` más abajo, que además limpia perfil y
        // roles. Adelantarlo aquí no aporta y duplicaría la regla.
        if (user === null) return anterior;
        // Mismo usuario (refresco de token cada ~50 min, volver del bloqueo del celular): no se
        // toca nada. Si no, la aplicación parpadearía a "Cargando…" sola cada tanto.
        if (anterior.user?.id === user.id) return anterior;
        // Sesión nueva: hay usuario y todavía no se sabe su perfil. `loading: true` es lo
        // honesto, y hace que el guard ESPERE en vez de concluir que no hay nadie.
        return { ...anterior, user, profile: null, roles: [], loading: true };
      });

      setTimeout(() => { void loadProfile(user, generacion); }, 0);
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  const marcarClaveCambiada = useCallback(() => {
    claveYaCambiada.current = true;
    setState((anterior) => ({ ...anterior, mustChangePassword: false }));
  }, []);

  const value = useMemo<AuthState>(
    () => ({ ...state, marcarClaveCambiada }),
    [state, marcarClaveCambiada],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Solo exige sesión iniciada. La usa `/cambiar-clave`, que tiene que ser alcanzable por
 *  cualquier autenticado — incluido quien todavía no puede pasar por `RequireRole`. */
export function RequireSesion({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Cargando…</div>;
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

export function RequireRole({ roles, children }: { roles: string[]; children: ReactNode }) {
  const { user, profile, roles: userRoles, loading, mustChangePassword } = useAuth();
  const { pathname } = useLocation();

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Cargando…</div>;
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  if (profile && profile.status !== 'activo') {
    return (
      <div className="p-8 text-center">
        <p className="text-lg font-semibold text-red-600">Cuenta no activa</p>
        <p className="mt-1 text-sm text-gray-500">
          {profile.status === 'bloqueado'
            ? `Tu cuenta está bloqueada${profile.block_reason ? `: ${profile.block_reason}` : ''}.`
            : 'Tu cuenta está inactiva.'}{' '}
          Contacta al jefe de operaciones.
        </p>
      </div>
    );
  }
  // Antes que el rol: mientras la clave siga siendo la que le dieron, cualquiera que sepa su
  // documento puede entrar como ella. La propia ruta de cambio queda exenta (si no, sería un
  // bucle); cerrar sesión también, porque es un botón, no una ruta.
  if (mustChangePassword && pathname !== RUTA_CAMBIAR_CLAVE) {
    return <Navigate to={RUTA_CAMBIAR_CLAVE} replace />;
  }
  if (!roles.some((r) => userRoles.includes(r))) {
    return (
      <div className="p-8 text-center">
        <p className="text-lg font-semibold text-red-600">Sin acceso</p>
        <p className="mt-1 text-sm text-gray-500">No tienes permisos para ver esta sección.</p>
      </div>
    );
  }
  return <>{children}</>;
}
